import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Divider,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlaceIcon from '@mui/icons-material/Place';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';
import EditIcon from '@mui/icons-material/Edit';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import usePriceOverview from '../../hooks/usePriceOverview';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const formatPrice = v => (typeof v === 'number' ? eur.format(v) : '—');

// Render a duration (stored in ms) as a compact German label.
const formatDuration = ms => {
  if (ms === null || ms === undefined) return '—';
  const totalMin = Math.round(ms / 60000);
  if (totalMin === 0) return '0 Min';
  if (totalMin % 60 === 0) return `${totalMin / 60} Std`;
  if (totalMin > 60) {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h} Std ${m} Min`;
  }
  return `${totalMin} Min`;
};

// Build the display rows/columns for a single price list by pivoting its price
// matrix into a category (rows) x duration (columns) grid. Declared categories
// and durations set the order; anything present only in the matrix is appended
// so no configured price is silently dropped.
function buildGrid(priceList) {
  const durationLabels = new Map();
  const bestsellerDurations = new Set();
  (priceList.durations || []).forEach(d => {
    durationLabels.set(d.durationMS, formatDuration(d.durationMS));
    if (d.isBestseller) bestsellerDurations.add(d.durationMS);
  });
  (priceList.matrix || []).forEach(m => {
    if (!durationLabels.has(m.durationMS)) durationLabels.set(m.durationMS, formatDuration(m.durationMS));
  });
  const durationCols = [...durationLabels.keys()].sort((a, b) => a - b);

  const catSeen = new Set();
  const categoryRows = [];
  (priceList.categories || [])
    .slice()
    .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0))
    .forEach(c => {
      if (!catSeen.has(c.name)) {
        catSeen.add(c.name);
        categoryRows.push({ name: c.name, shortName: c.shortName, hint: c.hint });
      }
    });
  (priceList.matrix || []).forEach(m => {
    if (!catSeen.has(m.category)) {
      catSeen.add(m.category);
      categoryRows.push({ name: m.category, shortName: '', hint: '' });
    }
  });

  const cellIndex = new Map();
  (priceList.matrix || []).forEach(m => {
    cellIndex.set(`${m.category}::${m.durationMS}`, m);
  });

  return { durationCols, durationLabels, bestsellerDurations, categoryRows, cellIndex };
}

// Render the tiered prices (by minimum person count) of one matrix cell.
function PriceCellContent({ entry }) {
  if (!entry || !Array.isArray(entry.prices) || entry.prices.length === 0) {
    return <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;
  }
  const tiers = entry.prices.slice().sort((a, b) => (a.minPersons || 0) - (b.minPersons || 0));
  if (tiers.length === 1) {
    return <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatPrice(tiers[0].price)}</Typography>;
  }
  return (
    <Stack spacing={0.25}>
      {tiers.map((t, i) => (
        <Typography key={i} variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
            ab {t.minPersons}:
          </Box>
          <Box component="span" sx={{ fontWeight: 700 }}>{formatPrice(t.price)}</Box>
        </Typography>
      ))}
    </Stack>
  );
}

function PriceListTable({ product, priceList, editable, onEditPrice }) {
  const { durationCols, durationLabels, bestsellerDurations, categoryRows, cellIndex } = useMemo(
    () => buildGrid(priceList),
    [priceList]
  );

  const hasGrid = durationCols.length > 0 && categoryRows.length > 0;

  return (
    <Box sx={{ mb: 2 }} className="price-list-block">
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 0.75 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {priceList.list || priceList.title || 'Preisliste'}
        </Typography>
        {priceList.groupType && (
          <Chip
            size="small"
            color={priceList.groupType === 'kids' ? 'info' : 'default'}
            label={priceList.groupType === 'kids' ? 'Kinder' : priceList.groupType === 'adults' ? 'Erwachsene' : priceList.groupType}
          />
        )}
        {priceList.fsk && <Chip size="small" variant="outlined" label={`FSK ${priceList.fsk}`} />}
        {typeof priceList.offlinePaymentFee === 'number' && priceList.offlinePaymentFee > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`Vor-Ort-Gebühr ${formatPrice(priceList.offlinePaymentFee)}`}
          />
        )}
      </Stack>

      {hasGrid ? (
        <Paper variant="outlined" sx={{ overflowX: 'auto', borderRadius: 1.5 }}>
          <Table size="small" className="price-table">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'action.hover', borderBottom: '2px solid', borderColor: 'divider' } }}>
                <TableCell sx={{ fontWeight: 700 }}>Kategorie</TableCell>
                {durationCols.map(d => (
                  <TableCell key={d} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      {bestsellerDurations.has(d) && (
                        <StarIcon sx={{ fontSize: 15, color: 'warning.main' }} titleAccess="Bestseller" />
                      )}
                      <span>{durationLabels.get(d)}</span>
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {categoryRows.map(cat => (
                <TableRow
                  key={cat.name}
                  sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' }, '&:last-child td': { border: 0 } }}
                >
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{cat.name}</Typography>
                    {cat.hint && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {cat.hint}
                      </Typography>
                    )}
                  </TableCell>
                  {durationCols.map(d => {
                    const entry = cellIndex.get(`${cat.name}::${d}`);
                    const canEdit = editable && entry && Array.isArray(entry.prices) && entry.prices.length > 0;
                    return (
                      <TableCell key={d} align="right" sx={{ verticalAlign: 'top' }}>
                        <Stack direction="row" spacing={0.5} alignItems="flex-start" justifyContent="flex-end">
                          <PriceCellContent entry={entry} />
                          {canEdit && (
                            <Tooltip title="Preis bearbeiten">
                              <IconButton
                                size="small"
                                className="no-print"
                                sx={{ p: 0.25, mt: '-2px' }}
                                onClick={() =>
                                  onEditPrice({
                                    productId: product.productId,
                                    productName: product.productName || product.internalName,
                                    pricelistId: priceList.pricelistId,
                                    listName: priceList.list || priceList.title || 'Preisliste',
                                    category: entry.category,
                                    durationMS: entry.durationMS,
                                    durationLabel: durationLabels.get(d),
                                    entryId: entry.entryId,
                                    prices: entry.prices,
                                  })
                                }
                              >
                                <EditIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          Keine Preise hinterlegt.
        </Typography>
      )}
    </Box>
  );
}

// Compact list of upgrade items (name → price) for one group, each editable.
function UpgradeGroup({ title, kind, items, product, editable, onEditUpgrade }) {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ minWidth: 220, flex: '1 1 240px' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Stack sx={{ mt: 0.5 }}>
        {items.map((it, i) => (
          <Stack
            key={it.upgradeId || i}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ py: 0.4, borderBottom: i < items.length - 1 ? '1px dashed' : 'none', borderColor: 'divider' }}
          >
            <Typography variant="body2">{it.name}</Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {formatPrice(it.price)}
              </Typography>
              {editable && (
                <Tooltip title="Upgrade-Preis bearbeiten">
                  <IconButton
                    size="small"
                    className="no-print"
                    sx={{ p: 0.25 }}
                    onClick={() =>
                      onEditUpgrade({
                        productId: product.productId,
                        productName: product.productName || product.internalName,
                        kind,
                        upgradeId: it.upgradeId,
                        name: it.name,
                        price: it.price,
                        priceOverridden: it.priceOverridden,
                      })
                    }
                  >
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function ProductUpgrades({ product, editable, onEditUpgrade }) {
  const upgrades = product.upgrades;
  if (!upgrades) return null;
  const { packages = [], upsells = [], singleOffer = null } = upgrades;
  if (packages.length === 0 && upsells.length === 0 && !singleOffer) return null;
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
      className="upgrades-block"
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        <LocalOfferIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Zusatzoptionen
        </Typography>
      </Stack>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <UpgradeGroup title="Pakete" kind="package" items={packages} product={product} editable={editable} onEditUpgrade={onEditUpgrade} />
        <UpgradeGroup title="Upsells" kind="upsell" items={upsells} product={product} editable={editable} onEditUpgrade={onEditUpgrade} />
        <UpgradeGroup title="Einzelangebot" kind="singleOffer" items={singleOffer ? [singleOffer] : []} product={product} editable={editable} onEditUpgrade={onEditUpgrade} />
      </Stack>
    </Box>
  );
}

function ProductBlock({ product, editable, onEditPrice, onEditUpgrade }) {
  return (
    <Box
      sx={{
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      className="product-block"
    >
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {product.productName || product.internalName}
        </Typography>
        {product.internalName && product.internalName !== product.productName && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ({product.internalName})
          </Typography>
        )}
      </Stack>
      {(product.priceLists || []).map(pl => (
        <PriceListTable
          key={pl.pricelistId}
          product={product}
          priceList={pl}
          editable={editable}
          onEditPrice={onEditPrice}
        />
      ))}
      <ProductUpgrades product={product} editable={editable} onEditUpgrade={onEditUpgrade} />
    </Box>
  );
}

function LocationBlock({ location, editable, onEditPrice, onEditUpgrade }) {
  return (
    <Paper
      elevation={0}
      sx={{ mb: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
      className="location-block"
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ px: 2, py: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText' }}
        className="location-header"
      >
        <PlaceIcon />
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, flexGrow: 1, fontSize: { xs: '1.15rem', sm: '1.5rem' }, wordBreak: 'break-word' }}
        >
          {location.branchName}
        </Typography>
        <Chip
          size="small"
          label={`${location.products.length} Produkt(e)`}
          sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit', fontWeight: 600, flexShrink: 0 }}
        />
      </Stack>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {location.products.map(p => (
          <ProductBlock
            key={p.productId}
            product={p}
            editable={editable}
            onEditPrice={onEditPrice}
            onEditUpgrade={onEditUpgrade}
          />
        ))}
      </Box>
    </Paper>
  );
}

// Impact panel: lists which locations/entertainments book a price list.
function ImpactPanel({ loading, impact }) {
  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Auswirkung wird ermittelt…</Typography>
      </Stack>
    );
  }
  if (!impact) return null;
  if (!impact.locations || impact.locations.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        Diese Preisliste ist aktuell keinem buchbaren Standort zugeordnet.
      </Alert>
    );
  }
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Diese Preisliste <b>„{impact.list}"</b> wird an <b>{impact.locations.length} Standort(en)</b> gebucht.
        Eine direkte Änderung wirkt sich auf alle folgenden aus:
      </Typography>
      <Stack spacing={0.5}>
        {impact.locations.map(loc => (
          <Box key={loc.branchId}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{loc.branchName}</Typography>
            {loc.entertainments.length > 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {loc.entertainments.join(', ')}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// Dialog to edit the prices of one matrix cell, then either apply in place
// (affects every usage) or decouple into a new bookable list.
function PriceEditDialog({ open, context, onClose, onSaved }) {
  const { getPricingImpact, updatePrice, decouplePricing } = usePriceOverview();
  const [values, setValues] = useState({});
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newListName, setNewListName] = useState('');

  useEffect(() => {
    if (!open || !context) return;
    const init = {};
    (context.prices || []).forEach(p => {
      init[p.priceId] = String(p.price);
    });
    setValues(init);
    // Default name for a decoupled list: suffix with the branch the edit was
    // started from, so the new list is recognizable as location-specific.
    const suffix = context.branchName ? `nur ${context.branchName}` : 'angepasst';
    setNewListName(context.listName ? `${context.listName} (${suffix})` : 'Neue Preisliste');
    setImpact(null);
    setImpactLoading(true);
    getPricingImpact(context.productId, context.pricelistId)
      .then(res => res && setImpact(res))
      .catch(() => setImpact(null))
      .finally(() => setImpactLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, context]);

  const changedRows = () =>
    (context?.prices || [])
      .map(p => ({ ...p, newPrice: parseFloat(values[p.priceId]) }))
      .filter(p => !isNaN(p.newPrice) && p.newPrice !== p.price);

  const handleApply = async () => {
    const changes = changedRows();
    if (!changes.length) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      for (const c of changes) {
        await updatePrice(context.productId, context.pricelistId, context.entryId, c.priceId, c.newPrice);
      }
      toast.success('Preis aktualisiert');
      onSaved();
    } catch (e) {
      toast.error('Fehler beim Aktualisieren des Preises');
    } finally {
      setSaving(false);
    }
  };

  const handleDecouple = async () => {
    const changes = changedRows().map(c => ({
      category: context.category,
      durationMS: context.durationMS,
      minPersons: c.minPersons,
      price: c.newPrice,
    }));
    if (!changes.length) {
      toast.info('Keine Preisänderung zum Auskoppeln.');
      return;
    }
    setSaving(true);
    try {
      await decouplePricing(context.productId, context.pricelistId, {
        changes,
        newListName: newListName || undefined,
      });
      toast.success('Preisänderung in neue Preisliste ausgekoppelt');
      onSaved();
    } catch (e) {
      toast.error('Fehler beim Auskoppeln der Preisänderung');
    } finally {
      setSaving(false);
    }
  };

  if (!context) return null;
  const hasChanges = changedRows().length > 0;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Preis bearbeiten
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {context.productName} · {context.listName} · {context.category} · {context.durationLabel}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {(context.prices || [])
            .slice()
            .sort((a, b) => (a.minPersons || 0) - (b.minPersons || 0))
            .map(p => (
              <TextField
                key={p.priceId}
                label={`Preis ab ${p.minPersons} Person(en)`}
                type="number"
                size="small"
                value={values[p.priceId] ?? ''}
                onChange={e => setValues(v => ({ ...v, [p.priceId]: e.target.value }))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                inputProps={{ step: '0.01', min: '0' }}
              />
            ))}
        </Stack>

        {!hasChanges && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            Ändere mindestens einen Preis, um „Übernehmen" oder „Auskoppeln" zu aktivieren.
          </Typography>
        )}

        <Divider sx={{ mb: 2 }}>Auswirkung</Divider>
        <ImpactPanel loading={impactLoading} impact={impact} />

        <Divider sx={{ my: 2 }}>Auskoppeln</Divider>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          Beim Auskoppeln wird die Änderung in eine <b>neue, buchbare Preisliste</b> übernommen und alle oben
          genannten Zuordnungen darauf umgestellt. Die bisherige Liste bleibt als <b>nicht mehr buchbare
          Referenz</b> erhalten – bestehende Buchungen (z. B. Personenzahl-Anpassungen) funktionieren damit weiter.
        </Typography>
        <TextField
          label="Name der neuen Preisliste"
          size="small"
          fullWidth
          value={newListName}
          onChange={e => setNewListName(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Abbrechen
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleDecouple}
          disabled={saving || !hasChanges}
          variant="outlined"
          startIcon={<CallSplitIcon />}
        >
          Auskoppeln
        </Button>
        <Button
          onClick={handleApply}
          disabled={saving || !hasChanges}
          variant="contained"
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Dialog to edit an upgrade price — either as a per-product override or as the
// global upgrade base price (which shows exactly which products are affected).
function UpgradeEditDialog({ open, context, onClose, onSaved }) {
  const authCtx = useContext(AuthContext);
  const { updateUpgradePrice, getUpgradeImpact, updateUpgradeGlobalPrice } = usePriceOverview();
  const [value, setValue] = useState('');
  const [scope, setScope] = useState('product');
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const canGlobal = authCtx.checkRight('upgrade:update');

  useEffect(() => {
    if (open && context) {
      setValue(context.price !== null && context.price !== undefined ? String(context.price) : '');
      setScope('product');
      setImpact(null);
    }
  }, [open, context]);

  const handleScopeChange = async newScope => {
    setScope(newScope);
    if (newScope === 'product') {
      setValue(context.price !== null && context.price !== undefined ? String(context.price) : '');
      return;
    }
    // Global: load impact and switch the field to the current base price.
    setImpactLoading(true);
    try {
      const res = await getUpgradeImpact(context.upgradeId);
      if (res) {
        setImpact(res);
        if (res.basePrice !== null && res.basePrice !== undefined) setValue(String(res.basePrice));
      }
    } catch (e) {
      setImpact(null);
    } finally {
      setImpactLoading(false);
    }
  };

  const handleSave = async () => {
    const num = parseFloat(value);
    setSaving(true);
    try {
      if (scope === 'global') {
        if (isNaN(num)) {
          toast.error('Bitte einen gültigen Preis eingeben.');
          setSaving(false);
          return;
        }
        await updateUpgradeGlobalPrice(context.upgradeId, num);
        toast.success('Globaler Upgrade-Preis aktualisiert');
      } else {
        await updateUpgradePrice(context.productId, {
          kind: context.kind,
          upgradeId: context.upgradeId,
          priceOverride: isNaN(num) ? null : num,
        });
        toast.success('Upgrade-Preis aktualisiert');
      }
      onSaved();
    } catch (e) {
      toast.error('Fehler beim Aktualisieren des Upgrade-Preises');
    } finally {
      setSaving(false);
    }
  };

  if (!context) return null;
  const kindLabel = context.kind === 'package' ? 'Paket' : context.kind === 'upsell' ? 'Upsell' : 'Einzelangebot';
  const overriddenProducts = (impact?.products || []).filter(p => p.hasPriceOverride);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Upgrade-Preis bearbeiten
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {context.productName} · {kindLabel}: {context.name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <FormControl sx={{ mb: 2 }}>
          <RadioGroup value={scope} onChange={e => handleScopeChange(e.target.value)}>
            <FormControlLabel
              value="product"
              control={<Radio size="small" />}
              label="Nur für dieses Produkt (Preisüberschreibung)"
            />
            <FormControlLabel
              value="global"
              control={<Radio size="small" />}
              disabled={!canGlobal}
              label={
                <span>
                  Für alle Produkte (globaler Upgrade-Preis)
                  {!canGlobal && (
                    <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 1 }}>
                      (Recht „upgrade:update" nötig)
                    </Typography>
                  )}
                </span>
              }
            />
          </RadioGroup>
        </FormControl>

        {scope === 'product' ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Der Preis gilt als produktbezogene Preisüberschreibung für <b>{context.productName}</b> – an allen
            Standorten, an denen dieses Produkt gebucht wird. Andere Produkte mit demselben Upgrade bleiben
            unverändert.
          </Typography>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              Der <b>Basispreis</b> des Upgrades „{context.name}" wird geändert. Das wirkt sich auf alle
              folgenden Produkte aus – <b>außer</b> auf Produkte mit eigener Preisüberschreibung:
            </Typography>
            {impactLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                <CircularProgress size={16} />
                <Typography variant="body2">Betroffene Produkte werden ermittelt…</Typography>
              </Stack>
            ) : impact ? (
              <>
                <Stack spacing={0.25} sx={{ mb: overriddenProducts.length ? 1 : 0 }}>
                  {impact.products.map(p => (
                    <Typography key={p.productId} variant="body2">
                      • {p.productName}
                      {p.hasPriceOverride && (
                        <Typography component="span" variant="caption" sx={{ color: 'warning.main', ml: 0.5 }}>
                          (eigene Preisüberschreibung – bleibt unverändert)
                        </Typography>
                      )}
                    </Typography>
                  ))}
                </Stack>
                {overriddenProducts.length > 0 && (
                  <Alert severity="info" variant="outlined">
                    {overriddenProducts.length} Produkt(e) haben eine eigene Preisüberschreibung und sind von
                    der Basispreis-Änderung nicht betroffen.
                  </Alert>
                )}
              </>
            ) : null}
          </Box>
        )}

        <TextField
          label={scope === 'global' ? 'Basispreis' : 'Preis'}
          type="number"
          size="small"
          fullWidth
          value={value}
          onChange={e => setValue(e.target.value)}
          InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
          inputProps={{ step: '0.01', min: '0' }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Abbrechen
        </Button>
        <Button onClick={handleSave} disabled={saving || (scope === 'global' && impactLoading)} variant="contained">
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PriceOverview() {
  const authCtx = useContext(AuthContext);
  const { getPriceOverview } = usePriceOverview();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [entertainmentTypeId, setEntertainmentTypeId] = useState('');
  const [priceEdit, setPriceEdit] = useState(null);
  const [upgradeEdit, setUpgradeEdit] = useState(null);

  const canRead =
    authCtx.checkRight('product:read') || authCtx.checkRight('product:read:priceOverviewOwn');
  const canEdit = authCtx.checkRight('product:update');

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await getPriceOverview();
      if (res) setData(res);
    } catch (err) {
      setLoadError(true);
      toast.error('Fehler beim Laden der Preisübersicht');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaved = () => {
    setPriceEdit(null);
    setUpgradeEdit(null);
    load();
  };

  // Entertainment types available for the current branch selection. With no branch
  // selected this is the global list; with a branch selected only the types actually
  // present at that location.
  const availableEntertainmentTypes = useMemo(() => {
    if (!data) return [];
    if (!branchId) return data.entertainmentTypes || [];
    const loc = (data.locations || []).find(l => l.branchId === branchId);
    if (!loc) return [];
    const byId = new Map();
    (loc.products || []).forEach(p =>
      (p.entertainmentTypes || []).forEach(t => byId.set(t.id, t.name))
    );
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [data, branchId]);

  // Reset the entertainment-type filter when the selected branch no longer offers it.
  useEffect(() => {
    if (entertainmentTypeId && !availableEntertainmentTypes.some(t => t.id === entertainmentTypeId)) {
      setEntertainmentTypeId('');
    }
  }, [availableEntertainmentTypes, entertainmentTypeId]);

  // Apply the branch and entertainment-type filters to the returned locations
  // without mutating the source data.
  const filteredLocations = useMemo(() => {
    if (!data) return [];
    return (data.locations || [])
      .filter(loc => !branchId || loc.branchId === branchId)
      .map(loc => {
        if (!entertainmentTypeId) return loc;
        const products = (loc.products || []).filter(p =>
          (p.entertainmentTypes || []).some(t => t.id === entertainmentTypeId)
        );
        return { ...loc, products };
      })
      .filter(loc => loc.products.length > 0);
  }, [data, branchId, entertainmentTypeId]);

  if (!canRead) return <Navigate to="/welcome" replace />;

  const isScoped = data?.scope === 'assignedBranches';

  return (
    <Box sx={viewRootTextPrimarySx} className="price-overview-print">
      {/* Print rules: only the overview is printed, the app shell is hidden. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .price-overview-print, .price-overview-print * { visibility: visible !important; }
          .price-overview-print { position: absolute; top: 0; left: 0; width: 100%; padding: 0 !important; }
          .no-print { display: none !important; }
          .location-block { break-inside: avoid-page; box-shadow: none !important; border: 1px solid #ccc !important; }
          .location-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .price-list-block, .product-block, .upgrades-block { break-inside: avoid; }
          .price-table { font-size: 11px; }
          @page { margin: 12mm; }
        }
      `}</style>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
        className="no-print"
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Preisübersicht
          </Typography>
          {data?.generatedAt && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Preise nach Standort · Stand {dayjs(data.generatedAt).format('DD.MM.YYYY HH:mm')}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={() => window.print()}
          disabled={loading || !filteredLocations.length}
          sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
        >
          Als PDF exportieren
        </Button>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 3 }}
        className="no-print"
      >
        <TextField
          select
          label="Standort"
          value={branchId}
          onChange={e => setBranchId(e.target.value)}
          sx={{ width: { xs: '100%', sm: 240 } }}
          size="small"
        >
          <MenuItem value="">Alle Standorte</MenuItem>
          {(data?.locations || []).map(loc => (
            <MenuItem key={loc.branchId} value={loc.branchId}>
              {loc.branchName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Entertainmenttyp"
          value={entertainmentTypeId}
          onChange={e => setEntertainmentTypeId(e.target.value)}
          sx={{ width: { xs: '100%', sm: 240 } }}
          size="small"
          disabled={!availableEntertainmentTypes.length}
        >
          <MenuItem value="">Alle Typen</MenuItem>
          {availableEntertainmentTypes.map(t => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        {isScoped && (
          <Chip size="small" color="info" variant="outlined" label="Nur zugewiesene Standorte" />
        )}
      </Stack>

      {/* Print-only header */}
      <Box sx={{ display: 'none', '@media print': { display: 'block', mb: 3 } }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Preisübersicht
        </Typography>
        {data?.generatedAt && (
          <Typography variant="body2">
            Stand {dayjs(data.generatedAt).format('DD.MM.YYYY HH:mm')}
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity="error" className="no-print">
          Die Preisübersicht konnte nicht geladen werden.
        </Alert>
      ) : !filteredLocations.length ? (
        <Alert severity="info" className="no-print">
          Keine Preislisten gefunden.
        </Alert>
      ) : (
        filteredLocations.map(loc => (
          <LocationBlock
            key={loc.branchId}
            location={loc}
            editable={canEdit}
            onEditPrice={ctx => setPriceEdit({ ...ctx, branchName: loc.branchName })}
            onEditUpgrade={setUpgradeEdit}
          />
        ))
      )}

      <PriceEditDialog
        open={!!priceEdit}
        context={priceEdit}
        onClose={() => setPriceEdit(null)}
        onSaved={handleSaved}
      />
      <UpgradeEditDialog
        open={!!upgradeEdit}
        context={upgradeEdit}
        onClose={() => setUpgradeEdit(null)}
        onSaved={handleSaved}
      />
    </Box>
  );
}
